import { isSessionTimingProjection, isActivityScheduleProjection } from './timing-projections.js';
import { isTypedDetails, isCurriculumPointer } from './typed-details.js';
import { isCertificationProjection } from './certification-contracts.js';
import { isVenueProjection } from './venue-responses.js';
export { isTypedDetails } from './typed-details.js';
/** A session endpoint returned a payload outside its published wire shape. */
export class SessionResponseContractError extends Error {
  constructor(operation, detail) {
    super(`Invalid ${operation} response: ${detail}`);
    this.name = 'SessionResponseContractError';
    this.code = 'INVALID_API_RESPONSE';
  }
}

const isRecord = value => value !== null && typeof value === 'object' && !Array.isArray(value);
const isId = value => typeof value === 'string' && value.length > 0;
const isRef = value => isId(value) || (isRecord(value) && isId(value._id));
const optionalText = value => value === undefined || value === null || typeof value === 'string';
const optionalRef = value => value === undefined || value === null || isRef(value);
const optionalDate = value => value === undefined || value === null
  || typeof value === 'string' && Number.isFinite(Date.parse(value));
const lessonScalar = value => value == null || typeof value === 'string' || Number.isFinite(value);
export const isCurriculumLesson = value => value == null || typeof value === 'string'
  || isRecord(value) && ['grade', 'lessonNumber', 'setCode'].every(field => lessonScalar(value[field]))
    && ['setTitle', 'set', 'lessonTitle', 'title'].every(field => optionalText(value[field]));
export const isPersonRef = value => isRef(value) && (!isRecord(value)
  || ['firstName', 'lastName', 'profilePicture', 'email'].every(field => optionalText(value[field]))
    && value.verificationCode == null
    && !['password', 'refreshToken', 'resetPasswordToken', 'resetPasswordExpires', 'emailVerificationCode', 'emailVerificationExpires'].some(field => Object.hasOwn(value, field)));
const isEntity = value => isRecord(value)
  && ['User', 'Member', 'Guest'].includes(value.type) && isPersonRef(value.refId)
  && (value.certifications == null || isCertificationProjection(value.certifications));
const isEntityList = value => Array.isArray(value) && value.every(isEntity);
export const isVenue = value => isId(value) || isVenueProjection(value);
const isVenueList = value => Array.isArray(value) && value.every(isVenue);
export const isVenueRequest = value => isRef(value) && (!isRecord(value)
  || (value.status === undefined || ['pending', 'approved', 'declined', 'cancelled', 'superseded'].includes(value.status))
    && (value.targetVenue === undefined || value.targetVenue === null || isVenue(value.targetVenue))
    && (value.kind === undefined || ['residence', 'communityVenueBooking'].includes(value.kind))
    && (value.approvalMode == null || ['once', 'ongoing'].includes(value.approvalMode))
    && ['requestedByUser', 'approverUser', 'decidedByUser', 'targetHousehold'].every(field => optionalRef(value[field]))
    && ['requiresAssemblyApproval', 'isRepeatRequest'].every(field => value[field] === undefined || typeof value[field] === 'boolean')
    && optionalDate(value.decidedAt) && optionalDate(value.supersededAt));
const isVenueRequestList = value => Array.isArray(value) && value.every(isVenueRequest);
const isActivityRef = value => isRef(value) && (!isRecord(value)
  || isActivityScheduleProjection(value) && (value.title === undefined || typeof value.title === 'string')
    && optionalRef(value.community)
    && (value.address == null || isRecord(value.address)
      && ['streetAddress', 'suburb', 'city', 'state', 'postalCode'].every(field => optionalText(value.address[field])))
    && (value.groupDetails == null || isRecord(value.groupDetails)
      && ['day', 'time', 'frequency'].every(field => optionalText(value.groupDetails[field]))));
// Older sessions can inherit a display time such as "7:00 PM" from an activity.
const isTime = value => typeof value === 'string' && value.trim().length > 0;

/** Session reads return the record directly; activity is populated on reads. */
export function readSessionRecordResponse(payload, operation = 'session detail') {
  if (!isRecord(payload) || !isId(payload._id)
    || !['Scheduled', 'Pending', 'Confirmed', 'Completed', 'Cancelled', 'Expired'].includes(payload.status)
    || !isId(payload.date) || Number.isNaN(Date.parse(payload.date))
    || !isEntityList(payload.participants) || !isEntityList(payload.facilitators)
    || !isActivityRef(payload.activity)) {
    throw new SessionResponseContractError(operation, 'expected a session with _id, activity, date, status, participants and facilitators.');
  }
  if (!isSessionTimingProjection(payload) || (payload.time != null && !isTime(payload.time))
    || !optionalRef(payload.community)
    || !optionalText(payload.onlineLink)
    || ['bookingEndsAt', 'createdAt', 'updatedAt', 'confirmationEmailSent', 'reminderSentAt'].some(field => !optionalDate(payload[field]))
    || (payload.pendingParticipants !== undefined && !isEntityList(payload.pendingParticipants))
    || (payload.pendingFacilitators !== undefined && !isEntityList(payload.pendingFacilitators))
    || (payload.venues !== undefined && !isVenueList(payload.venues))
    || (payload.venueRequests !== undefined && !isVenueRequestList(payload.venueRequests))
    || (payload.venueRequest != null && !isRef(payload.venueRequest))
    || !isTypedDetails(payload.details)
    || !isCurriculumLesson(payload.curriculumLesson)
    || payload.curriculumPointer != null && !isCurriculumPointer(payload.curriculumPointer)) {
    throw new SessionResponseContractError(operation, 'expected valid time, entity and venue relationships.');
  }
  return payload;
}

/** GET /api/sessions returns a direct array, including [] for an empty scope. */
export function readSessionListResponse(payload) {
  if (!Array.isArray(payload)) {
    throw new SessionResponseContractError('session list', 'expected Session[].');
  }
  payload.forEach(session => readSessionRecordResponse(session, 'session list item'));
  return payload;
}

/** POST /confirm reports the outcome and includes the confirmed session. */
export function readSessionConfirmationResponse(payload) {
  if (!isRecord(payload) || payload.success !== true
    || !['confirmed', 'already_confirmed'].includes(payload.outcome)
    || payload.resourceType !== 'session' || !isId(payload.activityId)) {
    throw new SessionResponseContractError('session confirmation', 'expected a successful session confirmation outcome and activityId.');
  }
  readSessionRecordResponse(payload.session, 'confirmed session');
  if (payload.session.activity !== payload.activityId) {
    throw new SessionResponseContractError('session confirmation', 'activityId must match the session activity.');
  }
  return payload;
}

/** POST /cancel wraps the updated record with a success message. */
export function readSessionCancellationResponse(payload) {
  if (!isRecord(payload) || !isId(payload.message)) {
    throw new SessionResponseContractError('session cancellation', 'expected { message, session }.');
  }
  readSessionRecordResponse(payload.session, 'cancelled session');
  return payload;
}

/** Delete and participation/facilitation actions acknowledge with a message. */
export function readSessionMessageResponse(payload, operation = 'session action') {
  if (!isRecord(payload) || !isId(payload.message)) {
    throw new SessionResponseContractError(operation, 'expected { message: string }.');
  }
  return payload;
}

/** Approval actions also return the updated session record. */
export function readSessionApprovalResponse(payload, operation = 'session approval') {
  readSessionMessageResponse(payload, operation);
  readSessionRecordResponse(payload.session, operation);
  return payload;
}
